// keytail-server + lumenlan-account 客户端（Web 应急端接真）。
// 登录走 account（密码登录拿 access token）；其余走 keytail-server（Bearer 鉴权，
// 服务端按 JWT subject 取身份）。所有响应解 {code,data,msg} 信封。
const SERVER_BASE = (import.meta.env.VITE_KEYTAIL_SERVER_URL || 'https://keytail.lumenlan.com').replace(/\/$/, '')
const ACCOUNT_BASE = (import.meta.env.VITE_LUMENLAN_ACCOUNT_URL || 'https://account.lumenlan.com').replace(/\/$/, '')
const API = '/v1/keytail'

let token = ''
// SSO 桥接：set 后所有 keytail-server Bearer 调用改从 provider 取 token（SDK 内存 access token，
// 已主动保鲜），不再用静态 token。未 set 时回落静态 token（loginPhone 回落路径 / 测试）。
let tokenProvider: (() => string) | null = null
export function setToken(t: string): void {
  token = t
}
export function clearToken(): void {
  token = ''
}
export function setTokenProvider(fn: (() => string) | null): void {
  tokenProvider = fn
}
function authToken(): string {
  return tokenProvider ? tokenProvider() : token
}

async function unwrap<T>(resp: Response): Promise<T> {
  const text = await resp.text()
  let body: any = null
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    /* 非 JSON */
  }
  if (!resp.ok) {
    const msg = (body && (body.error || body.msg)) || `HTTP ${resp.status}`
    const err = new Error(msg) as Error & { status?: number; body?: any }
    err.status = resp.status
    err.body = body
    throw err
  }
  // 信封 {code,data,msg} 或裸 body
  if (body && typeof body === 'object' && 'data' in body) return body.data as T
  return body as T
}

async function serverGet<T>(path: string): Promise<T> {
  const t = authToken()
  const resp = await fetch(`${SERVER_BASE}${API}${path}`, {
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  })
  return unwrap<T>(resp)
}
async function serverPost<T>(path: string, body: unknown): Promise<T> {
  const t = authToken()
  const resp = await fetch(`${SERVER_BASE}${API}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    },
    body: JSON.stringify(body ?? {}),
  })
  return unwrap<T>(resp)
}

// ---- account 登录 ----
// 注：真实 account 服务返回 access token 字段为 `token`（非 `accessToken`）；短信登录
// 走 /v1/auth/login/phone（smsCode）。密码登录 /login/password 在部分环境未上线，按需用。
export interface LoginResult {
  token?: string
  accessToken?: string
  refreshToken?: string
  user?: { id: string; nickname?: string }
}

function extractToken(data: LoginResult | null): string {
  return data?.token || data?.accessToken || ''
}

/** 发送短信验证码（测试环境响应 data.devCode 直接回显验证码）。 */
export async function sendSmsCode(phone: string): Promise<{ devCode?: string; expiresIn?: string }> {
  const resp = await fetch(`${ACCOUNT_BASE}/v1/auth/sms/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone }),
  })
  return unwrap(resp)
}

/** 短信验证码登录（主路径）。 */
export async function loginPhone(phone: string, smsCode: string): Promise<LoginResult> {
  const resp = await fetch(`${ACCOUNT_BASE}/v1/auth/login/phone`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, smsCode, deviceInfo: { platform: 'web', name: 'Keytail Web' } }),
  })
  const data = await unwrap<LoginResult>(resp)
  const t = extractToken(data)
  if (t) setToken(t)
  return data
}

/** 密码登录（部分环境提供；token 字段兼容 token/accessToken）。 */
export async function loginPassword(identifier: string, password: string): Promise<LoginResult> {
  const resp = await fetch(`${ACCOUNT_BASE}/v1/auth/login/password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, password, deviceInfo: { platform: 'web', name: 'Keytail Web' } }),
  })
  const data = await unwrap<LoginResult>(resp)
  const t = extractToken(data)
  if (t) setToken(t)
  return data
}

// ---- keytail-server ----
export interface MembershipView {
  mode: string
  planName: string
  tier: string
  cloudEnabled: boolean
  billingUrl?: string
}
export interface AccountView {
  userId: string
  subscriptionState: string
  member: boolean
  emergency: { dailyQuota: number; usedToday: number; remainingToday: number; unlockCardExpiresAt?: string }
  hasVault: boolean
  updatedAt: string
}
export interface VaultView {
  version: number
  vaultBlobB64: string
  updatedAt: string
}
export interface UnlockAssistView {
  sServerB64: string
  remainingToday: number
  unlockCardRequired: boolean
}

export const getMembership = () => serverGet<MembershipView>('/membership')
export const getAccount = () => serverGet<AccountView>('/account')
export const registerDevice = (deviceLabel: string) =>
  serverPost<unknown>('/account/register', { deviceLabel })

export async function fetchVault(): Promise<VaultView | null> {
  try {
    return await serverGet<VaultView>('/vault')
  } catch (e) {
    if ((e as { status?: number }).status === 404) return null
    throw e
  }
}

/**
 * 上传整包 vault（写数据面，门控付费会员）。OCC：baseVersion 必须等于服务端当前版本，
 * 否则 409 `version_conflict`（body.currentVersion = 服务端最新版本）。
 * 错误：403 = 非云会员（需开通）；502 = 权益检查不可达。由 sync 引擎按 status 分流。
 */
export const uploadVault = (baseVersion: number, vaultBlobB64: string) =>
  serverPost<VaultView>('/vault/upload', { baseVersion, vaultBlobB64 })

/** 从 api 错误里识别 OCC 版本冲突，并取出服务端当前版本（供三路合并重试）。 */
export function asVersionConflict(e: unknown): { currentVersion: number } | null {
  const err = e as { status?: number; body?: { error?: string; currentVersion?: number } }
  if (err?.status === 409 && err.body?.error === 'version_conflict') {
    return { currentVersion: Number(err.body.currentVersion ?? 0) }
  }
  return null
}

export const unlockAssist = (deviceId: string) =>
  serverPost<UnlockAssistView>('/vault/unlock_assist', { deviceId })

export const smsResetStart = (phone: string) =>
  serverPost<{ resetId: string; expiresIn: string }>('/sms/reset/start', { phone })

// ---- 设备管理（GET /device · POST /device/revoke）----
export interface DeviceView {
  id: string
  label: string
  kind: string
  enrolledAt: string
  lastSeenAt?: string
  revokedAt?: string
  sServerCached: boolean
  requiresSms2fa: boolean
}

export const listDevices = () =>
  serverGet<{ items: DeviceView[] }>('/device').then((r) => r?.items ?? [])

/** 撤销一台设备（服务端按 JWT subject 限定本人设备 → 撤他人设备得 404/403，防 BOLA）。 */
export const revokeDevice = (deviceId: string) =>
  serverPost<AccountView>('/device/revoke', { deviceId })

// ---- 云端访问审计（GET /audit · 只读）----
export interface AuditView {
  action: string
  detail?: string
  deviceId?: string
  createdAt: string
}

export const listAudit = (limit = 50) =>
  serverGet<{ items: AuditView[] }>(`/audit?limit=${limit}`).then((r) => r?.items ?? [])

// ---- 结算（瘦 BFF → 平台 payment；纯轮询，不消费事件）----
export interface SkuView {
  code: string
  name: string
  kind: string // subscription
  category: string // membership
  tier?: string
  periodDays?: number
  priceCents: number
  currency: string
  sortOrder: number
}
export interface OrderView {
  id: string
  skuCode: string
  skuKind: string
  amountCents: number
  currency: string
  channel: string
  status: string // pending | paid | failed | canceled | refunded
  codeUrl?: string // 微信 Native 二维码（生产）
  sandboxPayUrl?: string // 沙箱支付链接（测试）
  paidAt?: string
  createdAt?: string
}

export async function listSkus(): Promise<SkuView[]> {
  const res = await serverGet<{ items: SkuView[] }>('/billing/skus')
  return res?.items ?? []
}
export const createOrder = (skuCode: string, idempotencyKey: string) =>
  serverPost<OrderView>('/billing/orders', { skuCode, idempotencyKey })
export const getOrder = (id: string) => serverGet<OrderView>(`/billing/orders/${encodeURIComponent(id)}`)

// 解锁卡 = keytail_unlock_24h SKU 真实下单（同收银台流程）。
export const UNLOCK_CARD_SKU = 'keytail_unlock_24h'
export const purchaseUnlockCard = () =>
  serverPost<{ priceCny: number; orderId?: string; codeUrl?: string; sandboxPayUrl?: string; expiresAt?: string }>(
    '/unlock-card/purchase',
    {},
  )

export function newIdempotencyKey(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return 'idem_' + Date.now().toString(36) + Math.random().toString(36).slice(2)
  }
}
